import { useImperativeHandle, forwardRef, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector, useDispatch } from 'react-redux';
import { ConfigurationFormProps, ConfigurationFormData } from '../types';
import { RootState } from '@cp/src/store';
import FormFieldsRenderer from './FormRenderer/FormFieldsRenderer';
import { createCiuAllocationConfig, updateCiuAllocationConfigById } from '../action';
import styles from './index.module.scss';
import { isFunction } from '@cp/src/utils/commonUtils';

const ConfigurationForm = forwardRef((props: ConfigurationFormProps, ref) => {
  const {
    isDrawerOpen,
    onClose,
    onSuccess,
    setIsDirtyField,
    configurationData,
    setIsSubmitButtonDisabled
  } = props;

  const dispatch = useDispatch();

  const configurationSections = useSelector(
    (state: RootState) => state.ciuAllocations.formSections
  );

  const businessVertical = useSelector((store: RootState) => store?.common?.agentBusinessVertical);

  const selectedConfigurationId = useSelector(
    (state: RootState) => state.ciuAllocations.selectedConfigurationId
  );

  const {
    control,
    handleSubmit,
    formState: { errors, isDirty },
    reset,
    watch
  } = useForm<ConfigurationFormData>({
    mode: 'onChange',
    shouldFocusError: true,
    defaultValues: {
      allocationTeam: configurationData?.allocationTeam || '',
      allocationPurpose: configurationData?.allocationPurpose || '',
      caseDpdBucket: configurationData?.caseDpdBucket || '',
      caseTeam: configurationData?.caseTeam || '',
      caseExpiry: configurationData?.caseExpiry || '',
      allocationDates: configurationData?.allocationDates || '',
      dpdExclusion: configurationData?.dpdExclusion || '',
      emiOverdue: configurationData?.emiOverdue,
      emiAmount: configurationData?.emiAmount,
      lastMonthCollection: configurationData?.lastMonthCollection,
      currentMonthCollection: configurationData?.currentMonthCollection,
      posAmount: configurationData?.posAmount,
      currentMonthCosmosNcFeedback: configurationData?.currentMonthCosmosNcFeedback,
      currentMonthCosmosNcFeedbackDays: configurationData?.currentMonthCosmosNcFeedbackDays,
      currentMonthLonghornNcFeedback: configurationData?.currentMonthLonghornNcFeedback,
      currentMonthLonghornNcFeedbackDays: configurationData?.currentMonthLonghornNcFeedbackDays,
      lastMonthCosmosNcFeedback: configurationData?.lastMonthCosmosNcFeedback,
      lastMonthLonghornNcFeedback: configurationData?.lastMonthLonghornNcFeedback
    }
  });

  useEffect(() => {
    if (isFunction(setIsDirtyField)) {
      setIsDirtyField(isDirty);
    }
  }, [isDirty]);

  useEffect(() => {
    if (isDrawerOpen && configurationData) {
      reset({
        allocationTeam: configurationData.allocationTeam,
        allocationPurpose: configurationData.allocationPurpose,
        caseDpdBucket: configurationData.caseDpdBucket,
        caseTeam: configurationData.caseTeam,
        caseExpiry: configurationData.caseExpiry,
        allocationDates: configurationData.allocationDates,
        dpdExclusion: configurationData.dpdExclusion,
        emiOverdue: configurationData.emiOverdue,
        emiAmount: configurationData.emiAmount,
        lastMonthCollection: configurationData.lastMonthCollection,
        currentMonthCollection: configurationData.currentMonthCollection,
        posAmount: configurationData.posAmount,
        currentMonthCosmosNcFeedback: configurationData.currentMonthCosmosNcFeedback,
        currentMonthCosmosNcFeedbackDays: configurationData.currentMonthCosmosNcFeedbackDays,
        currentMonthLonghornNcFeedback: configurationData.currentMonthLonghornNcFeedback,
        currentMonthLonghornNcFeedbackDays: configurationData.currentMonthLonghornNcFeedbackDays,
        lastMonthCosmosNcFeedback: configurationData.lastMonthCosmosNcFeedback,
        lastMonthLonghornNcFeedback: configurationData.lastMonthLonghornNcFeedback
      });
    }
  }, [isDrawerOpen, configurationData]);

  const onSubmit = (data: ConfigurationFormData) => {
    if (errors.dpdExclusion) {
      return;
    }

    if (selectedConfigurationId) {
      dispatch(
        updateCiuAllocationConfigById(selectedConfigurationId, data, businessVertical, () => {
          onSuccess();
          onClose();
          reset();
        })
      );
    } else {
      dispatch(
        createCiuAllocationConfig(data, businessVertical, () => {
          onSuccess();
          onClose();
          reset();
        })
      );
    }
  };

  useImperativeHandle(ref, () => ({
    submitForm: () => {
      handleSubmit(onSubmit)();
    },
    getFormData: () => {
      const formValues = watch();
      return formValues;
    },
    resetForm: () => {
      reset();
    }
  }));

  return (
    <div className={styles.formContainer}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormFieldsRenderer
          configurationSections={configurationSections}
          configurationData={configurationData}
          control={control}
          errors={errors}
          isDrawerOpen={isDrawerOpen}
        />
      </form>
    </div>
  );
});

export default ConfigurationForm;
